<template>
    <vx-modal v-model:visible="visible" padding="standard" size="lg">
        <template v-slot:header>Tags</template>
        <div>
            <div class="flex gap-2 mb-3 lg:mb-6">
                <vx-selectable
                    size="lg"
                    placeholder=""
                    :options="options"
                    :loading="searching"
                    :taggable="true"
                    @search="search"
                    @open="!options.length && search()"
                    v-model="name"
                    @option:selected="add()"
                    :error="displayFirstError(validate.name)"
                >
                    <template v-slot:no-options> Search for a tag. </template>
                </vx-selectable>
                <vx-button @click="add()" :loading="adding" data-test="tag-add" size="lg" type="button">
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>

            <vx-alert v-if="error" color="danger" class="mb-3" :closeable="false">{{ error }}</vx-alert>
            <vx-icon-loading v-if="loadingTags && !tags.length" spin></vx-icon-loading>
            <vx-alert :closeable="false" v-else-if="!tags.length"> No tags were added to this contact. </vx-alert>
            <div v-else>
                <vx-list-animated v-model="tags" :removeable="false" @remove="remove"></vx-list-animated>
            </div>
        </div>
    </vx-modal>
</template>

<script setup>
import { tagsClient } from '~/services';
import debounce from '~/utils/debounce';
import { VxAlert, VxButton, VxIconLoading, VxListAnimated, VxModal, VxSelectable } from '@voxie/frontend-components';
import { ref, nextTick, watch } from 'vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { csvInjection, customAttributeKey, displayFirstError } from '~/utils/validation';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { catchRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;

const visible = defineModel('visible', {
    default: false,
});

const { tags, getTags, loadingTags, addTag, deleteTag } = useContactDetails();

watch(
    visible,
    () => {
        if (visible.value && !tags.value.length) {
            getTags();
        }
    },
    {
        immediate: true,
    }
);

const error = ref('');
const name = ref('');
const options = ref([]);
const searching = ref(false);
const adding = ref(false);

const search = debounce((search) => {
    searching.value = true;

    tagsClient
        .tagsAutocomplete(teamId, {
            query: search,
        })
        .then((response) => {
            options.value = response.data.data.map((tag) => tag.value).filter((tag) => !tags.value.includes(tag));
            searching.value = false;
        });
}, 200);

const validate = useVuelidate(
    {
        name: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(80),
            format: customAttributeKey,
            csvInjection: csvInjection,
        },
    },
    {
        name,
    }
);

const add = async () => {
    if (adding.value) {
        return;
    }
    adding.value = true;
    await nextTick();

    const isValid = await validate.value.$validate();
    if (!isValid) {
        setTimeout(() => {
            adding.value = false;
        }, 3500);
        return;
    }

    try {
        await addTag(name.value);

        name.value = '';
        adding.value = false;
    } catch (e) {
        error.value = catchRequestError(e);
    }
};

const remove = async (tag) => {
    try {
        await deleteTag(tag);
    } catch (e) {
        error.value = catchRequestError(e);
    }
};
</script>
